'use client';
import { useState } from 'react';
import { Wallet, FileText, Repeat, BarChart3, ArrowUpRight, ArrowDownRight, FileCheck2, Calendar } from 'lucide-react';
import styles from './GestaoModule.module.css';

export function GestaoModule({ reverse = false }: { reverse?: boolean }) {
  const [activeMockup, setActiveMockup] = useState('fluxo');

  const getCardStyle = (isActive: boolean) => ({
    display: 'flex', gap: '1.25rem', alignItems: 'flex-start', padding: '1.25rem',
    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.2s',
    border: isActive ? '1px solid var(--md-sys-color-primary)' : '1px solid #e2e8f0',
    background: isActive ? 'color-mix(in srgb, var(--md-sys-color-primary) 5%, transparent)' : 'white',
    boxShadow: isActive ? '0 4px 12px rgba(245,124,0,0.05)' : '0 2px 4px rgba(0,0,0,0.02)'
  });

  return (
    <div style={{ display: 'flex', flexDirection: reverse ? 'row-reverse' : 'row', gap: '4rem', alignItems: 'center', flexWrap: 'wrap' }}>
      
      {/* TEXTO E CARDS DE SELEÇÃO */}
      <div style={{ flex: '1 1 450px', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div>
          <span style={{background: '#dcfce7', color: '#166534', padding: '6px 16px', borderRadius: '50px', fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px'}}>
            Financeiro & Gestão
          </span>
          <h3 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', margin: '1.5rem 0 1rem 0', lineHeight: 1.2 }}>
            O Coração do seu Negócio
          </h3>
          <p style={{ fontSize: '1.1rem', color: '#64748b', lineHeight: 1.6 }}>
            Pare de errar contas no caderno. Domine seu fluxo de caixa, automatize cobranças recorrentes e emita Notas Fiscais com um clique.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem', maxHeight: '550px', overflowY: 'auto', paddingRight: '8px' }}>
          
          <div style={getCardStyle(activeMockup === 'fluxo')} onClick={() => setActiveMockup('fluxo')}>
            <Wallet size={28} style={{ color: '#10b981', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>Fluxo de Caixa e DRE</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Controle preciso de Contas a Pagar e Receber, com conciliação bancária.</p>
            </div>
          </div>
          
          <div style={getCardStyle(activeMockup === 'fiscal')} onClick={() => setActiveMockup('fiscal')}>
            <FileText size={28} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>Emissor Fiscal Inteligente</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Emissão de NFe, NFCe e NFSe automatizada, com envio direto para o contador.</p>
            </div>
          </div>

          <div style={getCardStyle(activeMockup === 'recorrencia')} onClick={() => setActiveMockup('recorrencia')}>
            <Repeat size={28} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>Assinaturas e Recorrência</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Crie planos mensais (PIX/Cartão). O sistema cobra e bloqueia inadimplentes sozinho.</p>
            </div>
          </div>

          <div style={getCardStyle(activeMockup === 'relatorios')} onClick={() => setActiveMockup('relatorios')}>
            <BarChart3 size={28} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: 700 }}>Relatórios Avançados</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.4 }}>Dashboards completos para você analisar o lucro e a saúde financeira da empresa.</p>
            </div>
          </div>

        </div>
      </div>

      {/* MOCKUP DESKTOP (DIREITA) */}
      <div style={{ flex: '1 1 450px', width: '100%', minWidth: 0 }}>
        <div className={styles.mockupContainer}>
          <div className={styles.desktopMockup}>
            
            {/* Barra do Navegador */}
            <div className={styles.browserBar}>
              <div className={styles.dots}><span></span><span></span><span></span></div>
              <div className={styles.addressBar}>
                painel.aesirerp.com/financeiro/{activeMockup}
              </div>
            </div>

            {/* Corpo do Dashboard */}
            <div className={styles.dashboardBody}>
              
              {/* 1. Fluxo de Caixa */}
              {activeMockup === 'fluxo' && (
                <>
                  <div className={styles.statsRow}>
                    <div className={styles.statCard}>
                      <span className={styles.statTitle}>Receitas (Mês)</span>
                      <span className={`${styles.statValue} ${styles.statGreen}`}>+ R$ 45.200,00</span>
                    </div>
                    <div className={styles.statCard}>
                      <span className={styles.statTitle}>Despesas (Mês)</span>
                      <span className={`${styles.statValue} ${styles.statRed}`}>- R$ 12.840,00</span>
                    </div>
                  </div>

                  <div className={styles.listContainer}>
                    <div className={styles.listHeader}>
                      <span>Lançamentos Recentes</span>
                    </div>
                    <div className={styles.listBody}>
                      <div className={styles.txRow}>
                        <div className={`${styles.txIcon} ${styles.iconIn}`}><ArrowDownRight size={18}/></div>
                        <div className={styles.txInfo}>
                          <span className={styles.txName}>Venda PDV #4420</span>
                          <span className={styles.txMeta}>PIX • Hoje, 14:30</span>
                        </div>
                        <div className={styles.txRight}>
                          <span className={`${styles.txAmount} ${styles.amountIn}`}>R$ 350,00</span>
                          <span className={`${styles.txStatus} ${styles.statusPaid}`}>RECEBIDO</span>
                        </div>
                      </div>
                      <div className={styles.txRow}>
                        <div className={`${styles.txIcon} ${styles.iconOut}`}><ArrowUpRight size={18}/></div>
                        <div className={styles.txInfo}>
                          <span className={styles.txName}>Conta de Luz (Enel)</span>
                          <span className={styles.txMeta}>Boleto • Vence em 2 dias</span>
                        </div>
                        <div className={styles.txRight}>
                          <span className={`${styles.txAmount} ${styles.amountOut}`}>R$ 840,50</span>
                          <span className={`${styles.txStatus} ${styles.statusPending}`}>AGUARDANDO</span>
                        </div>
                      </div>
                      <div className={styles.txRow}>
                        <div className={`${styles.txIcon} ${styles.iconIn}`}><ArrowDownRight size={18}/></div>
                        <div className={styles.txInfo}>
                          <span className={styles.txName}>Fatura Mercado Livre</span>
                          <span className={styles.txMeta}>Transferência • Ontem</span>
                        </div>
                        <div className={styles.txRight}>
                          <span className={`${styles.txAmount} ${styles.amountIn}`}>R$ 1.250,00</span>
                          <span className={`${styles.txStatus} ${styles.statusPaid}`}>RECEBIDO</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* 2. Emissor Fiscal */}
              {activeMockup === 'fiscal' && (
                <div className={styles.fiscalMockup}>
                  <div className={styles.fiscalTitle}>⚙️ Processando Lote NFe Sefaz...</div>
                  <div className={styles.xmlLine} style={{animationDelay: '0.1s'}}>&lt;<span className={styles.xmlTag}>nfeProc</span> versao=<span className={styles.xmlVal}>"4.00"</span>&gt;</div>
                  <div className={styles.xmlLine} style={{animationDelay: '0.3s'}}>&nbsp;&nbsp;&lt;<span className={styles.xmlTag}>NFe</span>&gt;</div>
                  <div className={styles.xmlLine} style={{animationDelay: '0.5s'}}>&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className={styles.xmlTag}>infNFe</span> Id=<span className={styles.xmlVal}>"NFe352304..."</span>&gt;</div>
                  <div className={styles.xmlLine} style={{animationDelay: '0.7s'}}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className={styles.xmlTag}>emit</span>&gt;</div>
                  <div className={styles.xmlLine} style={{animationDelay: '0.9s'}}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className={styles.xmlTag}>CNPJ</span>&gt;<span className={styles.xmlVal}>00.000.000/0001-00</span>&lt;/<span className={styles.xmlTag}>CNPJ</span>&gt;</div>
                  <div className={styles.xmlLine} style={{animationDelay: '1.1s'}}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className={styles.xmlTag}>xNome</span>&gt;<span className={styles.xmlVal}>AESIR ERP LTDA</span>&lt;/<span className={styles.xmlTag}>xNome</span>&gt;</div>
                  <div className={styles.xmlLine} style={{animationDelay: '1.3s'}}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;/<span className={styles.xmlTag}>emit</span>&gt;</div>
                  <div className={styles.xmlLine} style={{animationDelay: '1.5s'}}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className={styles.xmlTag}>total</span>&gt;...&lt;/<span className={styles.xmlTag}>total</span>&gt;</div>
                  
                  <div className={styles.fiscalStamp}>
                    <FileCheck2 size={16} style={{display: 'inline', marginRight: '4px', verticalAlign: 'text-bottom'}}/> 
                    AUTORIZADO O USO
                  </div>
                </div>
              )}

              {/* 3. Recorrências */}
              {activeMockup === 'recorrencia' && (
                <>
                  <div className={styles.listContainer}>
                    <div className={styles.listHeader}>
                      <span>Assinaturas Ativas (Cobrança Automática)</span>
                    </div>
                    <div className={styles.listBody}>
                      <div className={styles.txRow}>
                        <div className={`${styles.txIcon} ${styles.iconRec}`}><Repeat size={18}/></div>
                        <div className={styles.txInfo}>
                          <span className={styles.txName}>Plano Gold - João Silva</span>
                          <span className={styles.txMeta}><Calendar size={12}/> Renovação dia 15</span>
                        </div>
                        <div className={styles.txRight}>
                          <span className={styles.txAmount}>R$ 149,90/mês</span>
                          <span className={`${styles.txStatus} ${styles.statusPaid}`}>ATIVA</span>
                        </div>
                      </div>
                      <div className={styles.txRow}>
                        <div className={`${styles.txIcon} ${styles.iconRec}`}><Repeat size={18}/></div>
                        <div className={styles.txInfo}>
                          <span className={styles.txName}>Manutenção TI - Tech LTDA</span>
                          <span className={styles.txMeta}><Calendar size={12}/> Renovação dia 05</span>
                        </div>
                        <div className={styles.txRight}>
                          <span className={styles.txAmount}>R$ 800,00/mês</span>
                          <span className={`${styles.txStatus} ${styles.statusPending}`}>INADIMPLENTE</span>
                        </div>
                      </div>
                      <div className={styles.txRow}>
                        <div className={`${styles.txIcon} ${styles.iconRec}`}><Repeat size={18}/></div>
                        <div className={styles.txInfo}>
                          <span className={styles.txName}>Consultoria B2B</span>
                          <span className={styles.txMeta}><Calendar size={12}/> Renovação dia 20</span>
                        </div>
                        <div className={styles.txRight}>
                          <span className={styles.txAmount}>R$ 2.500,00/mês</span>
                          <span className={`${styles.txStatus} ${styles.statusPaid}`}>ATIVA</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* 4. Relatórios */}
              {activeMockup === 'relatorios' && (
                <>
                  <div className={styles.statsRow}>
                    <div className={styles.statCard}>
                      <span className={styles.statTitle}>Lucro Bruto</span>
                      <span className={`${styles.statValue} ${styles.statGreen}`}>R$ 32.360,00</span>
                    </div>
                    <div className={styles.statCard}>
                      <span className={styles.statTitle}>Crescimento</span>
                      <span className={styles.statValue} style={{color: '#3b82f6'}}>+ 15,4% 🚀</span>
                    </div>
                  </div>
                  
                  <div className={styles.chartWrapper}>
                    <div className={styles.chartBar} style={{height: '30%'}}></div>
                    <div className={styles.chartBar} style={{height: '45%'}}></div>
                    <div className={styles.chartBar} style={{height: '40%'}}></div>
                    <div className={`${styles.chartBar} ${styles.green}`} style={{height: '70%'}}></div>
                    <div className={`${styles.chartBar} ${styles.green}`} style={{height: '85%'}}></div>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>

    </div>
  );
}